import { useEffect, useState } from "react";
import { Play, Instagram, Dumbbell } from "lucide-react";

const isTall = (m) => m?.aspect === "9:16";

function Thumb({ m, onError }) {
  if (m.kind === "youtube") return <img src={m.thumb_url} alt="Exercise demonstration" onError={onError} className="w-full h-full object-cover" />;
  if (m.kind === "gif") return <img src={m.url} alt="Exercise demonstration" onError={onError} className="w-full h-full object-cover" />;
  if (m.kind === "video") return <video src={m.url} muted playsInline preload="metadata" onError={onError} className="w-full h-full object-cover" />;
  return (
    <div className="w-full h-full grid place-items-center bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737]">
      <Instagram size={28} className="text-white" />
    </div>
  );
}

export function ExercisePlaceholder() {
  return (
    <div className="w-full h-full min-h-0 grid place-items-center overflow-hidden bg-[linear-gradient(135deg,#17212f,#10151e)] text-slate-400">
      <div className="flex flex-col items-center gap-2 border border-white/10 px-6 py-4 text-center">
        <Dumbbell size={28} strokeWidth={1.5} className="text-[#FF6B45]" />
        <span className="font-mono-k text-[10px] uppercase tracking-[0.12em]">Exercise image</span>
      </div>
    </div>
  );
}

function Player({ m, onError }) {
  if (m.kind === "youtube" || m.kind === "instagram") {
    return <iframe src={m.embed_url} title="Exercise video" className="w-full h-full border-0" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen data-testid="exercise-media-iframe" />;
  }
  if (m.kind === "gif") return <img src={m.url} alt="Exercise demo" onError={onError} className="w-full h-full object-contain" data-testid="exercise-media-gif" />;
  return <video src={m.url} controls playsInline onError={onError} className="w-full h-full object-contain bg-black" data-testid="exercise-media-video" />;
}

// Thumbnail in a fixed 16:9 frame; vertical media sits centred at its true 9:16 shape.
export function MediaThumb({ media, imageUrl }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [media, imageUrl]);
  return (
    <div className="relative aspect-video bg-black rounded-md overflow-hidden flex justify-center">
      {imageUrl && !failed ? (
        <img src={imageUrl} alt="" onError={() => setFailed(true)} className="w-full h-full object-cover" />
      ) : !media || failed ? (
        <ExercisePlaceholder />
      ) : (
        <div className={isTall(media) ? "h-full aspect-[9/16]" : "w-full h-full"}><Thumb m={media} onError={() => setFailed(true)} /></div>
      )}
      {media && !failed && media.kind !== "gif" && !imageUrl && (
        <span className="absolute inset-0 grid place-items-center pointer-events-none">
          <span className="w-10 h-10 rounded-full bg-black/60 grid place-items-center"><Play size={16} className="text-white ml-0.5" /></span>
        </span>
      )}
    </div>
  );
}

export function MediaPlayer({ media, compact, imageUrl }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [media, imageUrl]);
  if ((!media || !media.url && !media.embed_url) && imageUrl && !failed) {
    return <div className="w-full aspect-video rounded-md overflow-hidden"><img src={imageUrl} alt="" onError={() => setFailed(true)} className="w-full h-full object-cover" /></div>;
  }
  if (!media || !media.url && !media.embed_url || failed) return <div className="w-full aspect-video rounded-md overflow-hidden"><ExercisePlaceholder /></div>;
  const tall = isTall(media);
  const size = tall ? (compact ? "h-[340px]" : "h-[min(70vh,640px)]") + " aspect-[9/16]" : "w-full aspect-video";
  return (
    <div className="flex justify-center bg-black rounded-md overflow-hidden" data-testid="exercise-media-player">
      <div className={`${size} max-w-full`}><Player m={media} onError={() => setFailed(true)} /></div>
    </div>
  );
}
